import { Ear, TriangleAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'
import { TEXT_CAPTION } from '../recipes'
import { CAPTION_LINE, WakeLockNote } from './VoiceSampleLive'

export interface VoiceRoomCheckProps {
  /** `measuring`: the room is listened to. `loud`: it was found too loud, and the count waits. */
  check: 'measuring' | 'loud'
  wakeLock: 'held' | 'unsupported'
}

const COPY = 'humint.voice.sample.room'

/** What stands under the caption: as tall as the topic it gives its place to, so nothing jumps. */
const BODY = cn(
  TEXT_CAPTION,
  'min-h-14 max-w-[36ch] text-center text-balance text-muted-foreground'
)

/** The room is listened to: nobody speaks for a moment. Told to a screen reader by the status line. */
function Measuring() {
  const { t } = useTranslation()
  return (
    <>
      <p className={cn(CAPTION_LINE, 'text-foreground')}>
        <Ear aria-hidden className="size-4 shrink-0" />
        {t(`${COPY}.measuring`)}
      </p>
      <p className={BODY}>{t(`${COPY}.measuringHint`)}</p>
    </>
  )
}

/** Too loud: said at once, as an alert, with what the operator can do about it. */
function Loud() {
  const { t } = useTranslation()
  return (
    <div role="alert" className="flex w-full flex-col items-center gap-4">
      <p className={cn(CAPTION_LINE, 'text-warning-foreground')}>
        <TriangleAlert aria-hidden className="size-4 shrink-0" />
        {t(`${COPY}.loud.title`)}
      </p>
      <p className={BODY}>{t(`${COPY}.loud.body`)}</p>
    </div>
  )
}

/**
 * Under the ring while the room is checked (ADR-0062), before anything is counted: the microphone
 * is open, the recording runs, and for about two seconds the room is listened to — the ring draws
 * that as a hairline going round inside it. A quiet room, or one with some noise, starts the count
 * by itself. One that is too loud stays here: the operator finds a quieter place and checks again,
 * or records all the same. It stands where the live caption and the topic will stand, at their
 * height, so the instrument does not move when the count starts.
 */
export function VoiceRoomCheck({ check, wakeLock }: VoiceRoomCheckProps) {
  return (
    <div className="flex w-full flex-col items-center gap-4">
      {check === 'loud' ? <Loud /> : <Measuring />}
      <WakeLockNote wakeLock={wakeLock} />
    </div>
  )
}
